import React, { useState } from 'react';
import { useMsal } from "@azure/msal-react";
import { Microscope, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { logoutFromMicrosoft } from '../lib/msGraph';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export default function MicrosoftLoginButton() {
  const { instance, accounts } = useMsal();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const loginRequest = {
        scopes: ["User.Read", "Calendars.ReadWrite"],
        prompt: "select_account"
      };

      const response = await instance.loginPopup(loginRequest);
      
      if (response) {
        instance.setActiveAccount(response.account);

        // Store Microsoft account in database
        if (user) {
          const { error } = await supabase
            .from('microsoft_accounts')
            .upsert({
              user_id: user.id,
              microsoft_id: response.account.localAccountId,
              email: response.account.username,
            }, {
              onConflict: 'user_id',
            });

          if (error) {
            console.error('Error storing Microsoft account:', error);
            toast.error('Fehler beim Speichern der Microsoft-Konto-Verbindung');
            return;
          }
        }

        toast.success(`Microsoft-Konto ${response.account.username} erfolgreich verbunden`);
      }
    } catch (error: any) {
      console.error("Error during Microsoft login:", error);
      
      if (error.errorCode === "user_cancelled") {
        toast.error('Anmeldung wurde abgebrochen');
      } else if (error.errorCode === "popup_window_error") {
        toast.error('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.');
      } else if (error.errorCode === "interaction_in_progress") {
        toast.error('Eine Anmeldesitzung ist bereits aktiv');
      } else if (error.errorCode === "monitor_window_timeout") {
        toast.error('Zeitüberschreitung bei der Anmeldung. Bitte versuchen Sie es erneut.');
      } else if (error.errorCode === "AADSTS50194") {
        toast.error('Diese Microsoft-Anwendung unterstützt keine persönlichen Microsoft-Konten. Bitte verwenden Sie ein Geschäfts-, Schul- oder Unikonto.');
      } else {
        toast.error(`Fehler bei der Microsoft-Anmeldung: ${error.message || 'Unbekannter Fehler'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logoutFromMicrosoft();

      // Remove Microsoft account from database
      if (user) {
        const { error } = await supabase
          .from('microsoft_accounts')
          .delete()
          .eq('user_id', user.id);

        if (error) {
          console.error('Error removing Microsoft account:', error);
          toast.error('Fehler beim Entfernen der Microsoft-Konto-Verbindung');
          return;
        }
      }

      toast.success('Microsoft-Konto erfolgreich getrennt');
    } catch (error: any) {
      console.error("Error during Microsoft logout:", error);
      toast.error('Fehler beim Trennen des Microsoft-Kontos');
    } finally {
      setIsLoading(false);
    }
  };

  if (accounts[0]) {
    return (
      <button
        onClick={handleLogout}
        disabled={isLoading}
        className="flex items-center justify-center w-full px-3 py-1.5 text-sm border border-red-300 rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
      >
        <LogOut className="w-4 h-4 mr-2" />
        {isLoading ? 'Trenne...' : 'Microsoft-Konto trennen'}
      </button>
    );
  }

  return (
    <button
      onClick={handleLogin}
      disabled={isLoading}
      className="flex items-center justify-center w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
    >
      <Microscope className="w-4 h-4 mr-2" />
      {isLoading ? 'Verbinde...' : 'Mit Microsoft verbinden'}
    </button>
  );
}